/**
 * The caller's answer ratings of one conversation: loads them once
 * (`agentFeedback(conversationId)`) and lets a footer store a change
 * (`setAgentFeedback`) — MEIS-13520, moved off `GET`/`POST /api/agent/
 * feedback`. A module-level cache keyed by conversationId dedupes the load
 * across every message's feedback control in the same conversation into a
 * single request, and lets a footer that mounts after the load already ran
 * read the result straight away.
 */
import { useApolloClient, type ApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import React from "react";
import {
  AgentFeedbackDocument,
  SetAgentFeedbackDocument,
  type AgentFeedbackQuery,
  type AgentFeedbackQueryVariables,
  type SetAgentFeedbackMutation,
  type SetAgentFeedbackMutationVariables,
} from "./useAgentFeedback.generated.js";

export type AgentFeedbackRating = { rating: 1 | -1; comment: string };
type AgentFeedbackRatings = Record<string, AgentFeedbackRating>;

const ratingsCache = new Map<string, Promise<AgentFeedbackRatings>>();

function loadRatings(
  apolloClient: ApolloClient<object>,
  conversationId: string
): Promise<AgentFeedbackRatings> {
  const cached = ratingsCache.get(conversationId);
  if (cached) return cached;
  const promise = apolloClient
    .query<AgentFeedbackQuery, AgentFeedbackQueryVariables>({
      query: AgentFeedbackDocument,
      variables: { conversationId },
      fetchPolicy: "network-only",
      context: { noErrorNotification: true },
    })
    .then(({ data }) => {
      const result = getDataOrNull(data.agentFeedback);
      const ratings: AgentFeedbackRatings = {};
      for (const entry of result?.ratings ?? []) {
        ratings[entry.messageId] = {
          rating: entry.rating === -1 ? -1 : 1,
          comment: entry.comment,
        };
      }
      return ratings;
    })
    .catch(err => {
      ratingsCache.delete(conversationId);
      throw err;
    });
  ratingsCache.set(conversationId, promise);
  return promise;
}

function withChange(
  ratings: AgentFeedbackRatings,
  messageId: string,
  value: 1 | -1 | 0,
  comment: string
): AgentFeedbackRatings {
  const next = { ...ratings };
  if (value === 0) delete next[messageId];
  else next[messageId] = { rating: value, comment };
  return next;
}

// A rating stored (or withdrawn) locally before the initial load resolves,
// keyed by messageId: `null` marks a withdrawal. Re-applied over the loaded
// snapshot so a store that wins the race against the query is not
// overwritten by the pre-rating state once the load resolves.
type AgentFeedbackOverrides = Record<string, AgentFeedbackRating | null>;

function withOverrides(
  ratings: AgentFeedbackRatings,
  overrides: AgentFeedbackOverrides
): AgentFeedbackRatings {
  const next = { ...ratings };
  for (const [messageId, override] of Object.entries(overrides)) {
    if (override === null) delete next[messageId];
    else next[messageId] = override;
  }
  return next;
}

export function useAgentFeedback({
  conversationId,
}: {
  conversationId: string | null;
}) {
  const apolloClient = useApolloClient();
  const [ratings, setRatings] = React.useState<AgentFeedbackRatings>({});
  const overridesRef = React.useRef<AgentFeedbackOverrides>({});

  React.useEffect(() => {
    overridesRef.current = {};
    if (!conversationId) return;
    let cancelled = false;
    loadRatings(apolloClient, conversationId)
      .then(loaded => {
        if (!cancelled) setRatings(withOverrides(loaded, overridesRef.current));
      })
      .catch(() => {
        // A failed load leaves the control unrated; the footer shows
        // nothing active rather than an error for a read that never
        // blocks the conversation.
      });
    return () => {
      cancelled = true;
    };
    // `apolloClient` is intentionally excluded: Apollo's own hook can hand
    // back a new client reference on every render (see useAgentAccess.ts),
    // which would otherwise re-run this load on every render instead of
    // only when the conversation changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  const store = React.useCallback(
    async (messageId: string, rating: 1 | -1 | 0, comment: string) => {
      const { data } = await apolloClient.mutate<
        SetAgentFeedbackMutation,
        SetAgentFeedbackMutationVariables
      >({
        mutation: SetAgentFeedbackDocument,
        variables: { input: { messageId, rating, comment } },
        context: { noErrorNotification: true },
      });
      // The server refuses (`stored: false`) a rating of a message that is
      // no longer an assistant answer of the caller's own conversation;
      // throwing leaves overrides, state and cache untouched so the
      // footer's catch path shows the rating error. A refused withdrawal
      // (`rating: 0`) already is the asked-for end state — nothing is held.
      if (rating !== 0 && data?.setAgentFeedback.stored !== true) {
        throw new Error("The rating was not stored");
      }
      overridesRef.current[messageId] =
        rating === 0 ? null : { rating, comment };
      setRatings(current => withChange(current, messageId, rating, comment));
      if (conversationId) {
        const cached = ratingsCache.get(conversationId) ?? Promise.resolve({});
        ratingsCache.set(
          conversationId,
          cached.then(loaded => withChange(loaded, messageId, rating, comment))
        );
      }
    },
    [apolloClient, conversationId]
  );

  return { ratings, store };
}
